/**
 * SPECS: __tests__/admin-route-registry.test.ts
 *
 * CANONICAL: admin-route-registry
 *
 * Server-safe inventory for Mission Control administration and settings routes.
 * Keep React components out of this module: icon keys are resolved by the client
 * navigation layer through the existing shared Lucide icon map.
 */
import { PRIMARY_ADMIN_ROUTES } from './admin-route-definitions-primary';
import { SECONDARY_ADMIN_ROUTES } from './admin-route-definitions-secondary';
import {
  ADMIN_ROUTE_GROUPS,
  type AdminNavigationGroup,
  type AdminRouteDefinition,
  type AdminRouteGroupDefinition,
  type AdminRouteGroupId,
} from './admin-route-types';

export * from './admin-route-types';

/**
 * Every page under app/(dashboard)/admin and app/(dashboard)/settings is
 * represented here. Cross-hub destinations are included when they are the
 * canonical home for an administrative task.
 */
export const ADMIN_ROUTE_REGISTRY = [
  ...PRIMARY_ADMIN_ROUTES,
  ...SECONDARY_ADMIN_ROUTES,
] as const satisfies readonly AdminRouteDefinition[];

export type SearchParamsReader = Pick<URLSearchParams, 'get'>;

const groupById = new Map<AdminRouteGroupId, AdminRouteGroupDefinition>(
  ADMIN_ROUTE_GROUPS.map((group) => [group.id, group]),
);

/** Return the concrete canonical destination for a route, when one exists. */
export function getAdminRouteDestination(route: AdminRouteDefinition): string | undefined {
  return route.canonicalHref ?? route.href;
}

/** Primary navigation is deliberately restricted to canonical, concrete routes. */
export function getAdminNavigationGroups(): AdminNavigationGroup[] {
  return ADMIN_ROUTE_GROUPS.map((group) => ({
    ...group,
    routes: ADMIN_ROUTE_REGISTRY.filter(
      (route) => route.group === group.id
        && route.lifecycle === 'canonical'
        && route.visibility.navigation
        && Boolean(getAdminRouteDestination(route)),
    ),
  })).filter((group) => group.routes.length > 0);
}

function normalizeSearchText(value: string): string {
  return value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Search the retained, concrete admin destinations by task language and synonyms. */
export function getAdminSearchRoutes(query = ''): AdminRouteDefinition[] {
  const tokens = normalizeSearchText(query).split(/\s+/).filter(Boolean);

  return ADMIN_ROUTE_REGISTRY.filter((route) => {
    if (!route.visibility.commandPalette || route.lifecycle === 'retired') return false;
    if (!getAdminRouteDestination(route)) return false;
    if (tokens.length === 0) return true;

    const group = groupById.get(route.group);
    const haystack = normalizeSearchText([
      route.label,
      route.description,
      group?.label ?? '',
      ...route.searchTerms,
    ].join(' '));

    return tokens.every((token) => haystack.includes(token));
  });
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function routePatternToRegExp(pathPattern: string, includeDescendants: boolean): RegExp {
  const escapedSegments = pathPattern
    .split('/')
    .map((segment) => (/^\[.+\]$/.test(segment) ? '[^/]+' : escapeRegExp(segment)))
    .join('/');
  return new RegExp(`^${escapedSegments}${includeDescendants ? '(?:/.*)?' : ''}/?$`);
}

/**
 * Match pathname and any required query state. Query-backed hub destinations
 * (for example Money → Economics) therefore cannot activate the wrong tab.
 */
export function isAdminRouteActive(
  route: AdminRouteDefinition,
  pathname: string,
  searchParams: SearchParamsReader,
): boolean {
  const activeWhen = route.activeWhen;
  const routePath = activeWhen?.pathname ?? route.pathPattern;
  const includeDescendants = activeWhen?.includeDescendants ?? !routePath.includes('[');

  if (!routePatternToRegExp(routePath, includeDescendants).test(pathname)) return false;

  return Object.entries(activeWhen?.query ?? {}).every(
    ([key, expected]) => searchParams.get(key) === expected,
  );
}
